import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { HelpCircle, Mail, MapPin } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { SEO, getFAQSchema, getBreadcrumbSchema } from '@/hooks/useSEO';
import { useLanguage } from '@/hooks/useLanguage';

export default function FaqPage() {
  const { t } = useTranslation();
  const { lang } = useLanguage();

  const faqCategories = [
    {
      titleKey: 'information.faq.categories.addressesPostal',
      questions: [
        { q: 'information.faq.questions.howOftenChanges.q', a: 'information.faq.questions.howOftenChanges.a' },
        { q: 'information.faq.questions.followBorders.q', a: 'information.faq.questions.followBorders.a' },
        { q: 'information.faq.questions.howDetermined.q', a: 'information.faq.questions.howDetermined.a' },
        { q: 'information.faq.questions.structure.q', a: 'information.faq.questions.structure.a' },
        { q: 'information.faq.questions.findPostalCode.q', a: 'information.faq.questions.findPostalCode.a' },
        { q: 'information.faq.questions.howWritten.q', a: 'information.faq.questions.howWritten.a' },
      ],
    },
    {
      titleKey: 'information.faq.categories.orderSubscription',
      questions: [
        { q: 'information.faq.questions.whenDelivered.q', a: 'information.faq.questions.whenDelivered.a' },
        { q: 'information.faq.questions.cancelSubscription.q', a: 'information.faq.questions.cancelSubscription.a' },
        { q: 'information.faq.questions.fileUsage.q', a: 'information.faq.questions.fileUsage.a' },
      ],
    },
  ];

  return (
    <div className="container py-12 lg:py-16">
      <SEO
        title={t('information.faq.title')}
        description={t('meta.faq')}
        path="/information/faq"
        jsonLd={[
          getBreadcrumbSchema([
            { name: t('common.home'), url: `https://postnummerservice.se/${lang}` },
            { name: t('common.information'), url: `https://postnummerservice.se/${lang}/information/faq` },
            { name: t('information.faq.title'), url: `https://postnummerservice.se/${lang}/information/faq` },
          ]),
          getFAQSchema(
            faqCategories.flatMap((cat) =>
              cat.questions.map((q) => ({
                question: t(q.q),
                answer: t(q.a),
              }))
            )
          ),
        ]}
      />
      <div className="grid gap-12 lg:grid-cols-3">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-8">
          {/* Hero Section */}
          <section>
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 rounded-lg bg-secondary/10 flex items-center justify-center flex-shrink-0">
                <HelpCircle className="w-6 h-6 text-secondary" />
              </div>
              <div>
                <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-2">
                  {t('information.faq.title')}
                </h1>
                <p className="text-lg text-muted-foreground leading-relaxed">
                  {t('information.faq.subtitle')}
                </p>
              </div>
            </div>
          </section>

          {/* FAQ Categories */}
          {faqCategories.map((category, categoryIndex) => (
            <section key={categoryIndex}>
              <h2 className="text-xl font-semibold mb-4">{t(category.titleKey)}</h2>
              <Accordion type="single" collapsible className="w-full">
                {category.questions.map((faq, index) => (
                  <AccordionItem key={index} value={`${categoryIndex}-${index}`}>
                    <AccordionTrigger className="text-left hover:text-secondary">
                      {t(faq.q)}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground leading-relaxed">
                      {t(faq.a)}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </section>
          ))}
        </div>

        {/* Sidebar */}
        <aside className="lg:col-span-1 space-y-4">
          <div className="sticky top-24 space-y-4">
            <Card>
              <CardContent className="p-6">
                <h3 className="font-semibold mb-4">{t('information.faq.stillQuestions')}</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  {t('information.faq.contactUs')}
                </p>
                <div className="space-y-3">
                   <div className="flex items-center gap-3 text-sm">
                    <Mail className="w-4 h-4 text-muted-foreground" />
                    <a href="mailto:info@postnummerservice.se" className="hover:text-secondary transition-colors">
                      info@postnummerservice.se
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-3">
                  <MapPin className="w-4 h-4 text-secondary" />
                  <h3 className="font-semibold">{t('navigation.postalCodes')}</h3>
                </div>
                <p className="text-sm text-muted-foreground mb-3">
                  {t('information.faq.postalSidebarDesc')}
                </p>
                <Link to={`/${lang}/information/postnummer`} className="text-sm text-secondary hover:underline">
                  {t('information.faq.postalSidebarLink')}
                </Link>
              </CardContent>
            </Card>
          </div>
        </aside>
      </div>
    </div>
  );
}
